/**
 * Pure helpers that turn a chat `Message` (live or persisted) into what the
 * bubble renders. DOM-free so they are unit-testable without the route /
 * component stack.
 */

import type { Message } from '../chat-types';
import type { MarkerCitation } from '../citation-marker';

/**
 * The citation fields MarkdownRenderer needs to resolve an inline "[Source N]"
 * marker, INCLUDING the server-stamped `sourceIndex` (review BF-01: the field
 * list in chat-messages.tsx silently dropped the stamp, so the renderer always
 * fell back to the positional read and "[Source 4]" on a 3-entry list vanished
 * — the exact ai1 case the index-stability fix was for).
 */
export function toRendererCitations(
  citations: Message['citations'],
): MarkerCitation[] | undefined {
  return citations?.map((c) => ({
    docName: c.docName,
    page: c.page,
    section: c.section,
    text: c.text,
    score: c.score,
    // source-attribution: keep the tier so inline [N] badges render
    // blue/amber/teal (verified-vs-unverified trust in the prose).
    sourceType: c.sourceType,
    sourceIndex: typeof c.sourceIndex === 'number' ? c.sourceIndex : undefined,
  }));
}

/**
 * The banner to show above a PERSISTED assistant message, from the `banners`
 * array the api's audit_logging filter writes into message metadata (review
 * BF-11: a truth-critical "No record was created…" notice was stream-only, so
 * a reloaded or shared conversation rendered the fabricated work order with
 * no warning). The most severe banner wins (error > warning > info); within a
 * level the latest one wins.
 */
export function bannerFromMetadata(
  meta: Record<string, unknown> | null | undefined,
): NonNullable<Message['qualityBanner']> | undefined {
  const raw = meta && Array.isArray((meta as { banners?: unknown }).banners)
    ? (meta as { banners: unknown[] }).banners
    : [];
  const banners = raw
    .filter(
      (b): b is { level?: unknown; kind?: unknown; message?: unknown } =>
        !!b && typeof b === 'object',
    )
    .map((b) => ({
      level: typeof b.level === 'string' ? b.level : 'info',
      kind: typeof b.kind === 'string' ? b.kind : 'answer_quality',
      message: typeof b.message === 'string' ? b.message : '',
    }))
    .filter((b) => b.message.trim() !== '');
  if (banners.length === 0) return undefined;
  const rank = (level: string): number =>
    level === 'error' ? 3 : level === 'warning' ? 2 : 1;
  // reverse first so that, at equal rank, the LATEST banner is kept (sort is stable)
  return [...banners].reverse().sort((a, b) => rank(b.level) - rank(a.level))[0];
}
